import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useActiveOrganization } from './useActiveOrganization';

export type UserRole = 'admin' | 'secretary' | 'doctor' | null;

export interface RolePermissions {
  canViewDashboard: boolean;
  canViewPatients: boolean;
  canCreatePatients: boolean;
  canEditPatients: boolean;
  canDeletePatients: boolean;
  canViewPatientRecords: boolean;
  canAddEvolutions: boolean;
  canAddPhotos: boolean;
  canAddDocuments: boolean;
  canViewSchedule: boolean;
  canCreateAppointments: boolean;
  canEditAppointments: boolean;
  canDeleteAppointments: boolean;
  canViewFinance: boolean;
  canCreateTransactions: boolean;
  canEditTransactions: boolean;
  canDeleteTransactions: boolean;
  canViewBudgets: boolean;
  canCreateBudgets: boolean;
  canEditBudgets: boolean;
  canDeleteBudgets: boolean;
  canManageOrganization: boolean;
  canManageUsers: boolean;
  canViewSettings: boolean;
}

const ROLE_PERMISSIONS: Record<Exclude<UserRole, null>, RolePermissions> = {
  admin: {
    canViewDashboard: true,
    canViewPatients: true,
    canCreatePatients: true,
    canEditPatients: true,
    canDeletePatients: true,
    canViewPatientRecords: true,
    canAddEvolutions: true,
    canAddPhotos: true,
    canAddDocuments: true,
    canViewSchedule: true,
    canCreateAppointments: true,
    canEditAppointments: true,
    canDeleteAppointments: true,
    canViewFinance: true,
    canCreateTransactions: true,
    canEditTransactions: true,
    canDeleteTransactions: true,
    canViewBudgets: true,
    canCreateBudgets: true,
    canEditBudgets: true,
    canDeleteBudgets: true,
    canManageOrganization: true,
    canManageUsers: true,
    canViewSettings: true,
  },
  secretary: {
    canViewDashboard: true,
    canViewPatients: true,
    canCreatePatients: true,
    canEditPatients: true,
    canDeletePatients: false, // Secretária não pode deletar pacientes
    canViewPatientRecords: true,
    canAddEvolutions: false, // Apenas médicos podem adicionar evoluções
    canAddPhotos: true,
    canAddDocuments: true,
    canViewSchedule: true,
    canCreateAppointments: true,
    canEditAppointments: true,
    canDeleteAppointments: false, // Secretária não pode deletar agendamentos
    canViewFinance: true,
    canCreateTransactions: true,
    canEditTransactions: true,
    canDeleteTransactions: false, // Secretária não pode deletar transações
    canViewBudgets: true,
    canCreateBudgets: true,
    canEditBudgets: true,
    canDeleteBudgets: false,
    canManageOrganization: false,
    canManageUsers: false,
    canViewSettings: false, // Secretária não pode acessar configurações
  },
  doctor: {
    canViewDashboard: true,
    canViewPatients: true,
    canCreatePatients: true,
    canEditPatients: true,
    canDeletePatients: false, // Médico não pode deletar pacientes
    canViewPatientRecords: true,
    canAddEvolutions: true, // Médicos podem adicionar evoluções
    canAddPhotos: true,
    canAddDocuments: true,
    canViewSchedule: true,
    canCreateAppointments: true,
    canEditAppointments: true,
    canDeleteAppointments: false,
    canViewFinance: false, // Médico não pode ver finanças
    canCreateTransactions: false,
    canEditTransactions: false,
    canDeleteTransactions: false,
    canViewBudgets: true, // Médico pode ver orçamentos (relacionados aos pacientes)
    canCreateBudgets: true,
    canEditBudgets: true,
    canDeleteBudgets: false,
    canManageOrganization: false,
    canManageUsers: false,
    canViewSettings: false,
  },
};

export function useUserRole() {
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user || !activeOrganizationId) {
        setRole(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Primeiro verificar se o usuário é super_admin global
        const { data: superAdminData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'super_admin')
          .maybeSingle();

        if (superAdminData) {
          // Super admin é tratado como admin em qualquer organização
          setRole('admin');
          setLoading(false);
          return;
        }

        // Buscar o role do usuário na organização ativa
        const { data, error } = await supabase
          .from('organization_members')
          .select('role')
          .eq('user_id', user.id)
          .eq('organization_id', activeOrganizationId)
          .single();

        if (error) {
          console.error('Erro ao buscar role do usuário:', error);
          setRole(null);
        } else {
          setRole(data.role as UserRole);
        }
      } catch (error) {
        console.error('Erro ao buscar role:', error);
        setRole(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user, activeOrganizationId]);

  const getPermissions = (): RolePermissions | null => {
    if (!role) return null;
    return ROLE_PERMISSIONS[role];
  };

  const hasPermission = (permission: keyof RolePermissions): boolean => {
    const permissions = getPermissions();
    return permissions ? permissions[permission] : false;
  };

  return {
    role,
    loading,
    permissions: getPermissions(),
    hasPermission,
    isAdmin: role === 'admin',
    isSecretary: role === 'secretary',
    isDoctor: role === 'doctor',
  };
}
